import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { formatDate } from '../utils/helpers';
import SendContractModal from '../components/modals/SendContractModal';

const SentContracts = () => {
  const context = useCRM();
  const sentContracts = context?.sentContracts || [];
  const companies = context?.companies || [];
  const simulateDocumentView = context?.simulateDocumentView || (() => {});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Alle');
  const [selectedContract, setSelectedContract] = useState(null);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const filteredContracts = useMemo(() => {
    if (!sentContracts) return [];
    return sentContracts.filter(contract => {
      if (!contract) return false;
      const company = companies.find(c => c.company_id === contract.company_id);
      const matchesSearch = (company?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (contract.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = activeFilter === 'Alle' || (activeFilter === 'Signert' && contract.status === 'signed') || (activeFilter === 'Venter' && contract.status === 'sent') || (activeFilter === 'Vist' && contract.status === 'viewed');
      return matchesSearch && matchesFilter;
    });
  }, [sentContracts, searchQuery, activeFilter, companies]);

  const handleCopyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'signed': return (
        <span className="flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100">
          <SafeIcon icon="✅" className="w-3 h-3" /> Signert
        </span>
      );
      case 'viewed': return (
        <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100">
          <SafeIcon icon="👁️" className="w-3 h-3" /> Vist
        </span>
      );
      default: return (
        <span className="flex items-center gap-1.5 px-3 py-1 bg-slate-50 text-slate-500 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-100">
          <SafeIcon icon="🕒" className="w-3 h-3" /> Sendt
        </span>
      );
    }
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">E-Signering & Dokumentsporing</h1>
          <p className="text-slate-500 font-medium mt-1">Del dokumenter via link og spor hver eneste visning i sanntid.</p>
        </div>
        <button onClick={() => setIsSendModalOpen(true)} className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white font-black rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-200 transition-all active:scale-95">
          <SafeIcon icon="📤" className="w-5 h-5" /> Send eller Del dokument
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-12 xl:col-span-8 space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <SafeIcon icon="🔍" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input type="text" placeholder="Søk i dokumenter..." className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-primary-500/10 outline-none font-bold text-sm transition-all text-slate-900" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="flex gap-2">
              {['Alle', 'Signert', 'Venter', 'Vist'].map(filter => (
                <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all border ${activeFilter === filter ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Mottaker</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Visninger</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Siste hendelse</th>
                  <th className="px-6 py-4 text-slate-900"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredContracts.map((contract) => {
                  if (!contract) return null;
                  const company = companies.find(c => c.company_id === contract.company_id);
                  const isSelected = selectedContract?.id === contract.id;
                  const logs = contract.tracking_log || [];
                  const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;
                  return (
                    <tr key={contract.id} className={`hover:bg-slate-50 transition-colors cursor-pointer ${isSelected ? 'bg-primary-50/50' : ''}`} onClick={() => setSelectedContract(contract)}>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
                            <SafeIcon icon="📄" className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 text-sm">{company?.name || 'Direkte deling'}</p>
                            <p className="text-xs text-slate-500 font-medium">{contract.contact_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">{getStatusBadge(contract.status)}</td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-1.5 text-sm font-black text-slate-700">
                          <SafeIcon icon="👁️" className="w-4 h-4 text-slate-400" /> {contract.view_count || 0}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <p className="text-xs text-slate-500 font-medium">
                          {lastLog ? formatDate(lastLog.date) : 'Ingen logg'}
                        </p>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <SafeIcon icon="➡️" className="w-5 h-5 text-slate-300 ml-auto" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-12 xl:col-span-4 space-y-6">
          <AnimatePresence mode="wait">
            {selectedContract ? (
              <motion.div key="tracking" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-6">
                <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
                  <h3 className="font-black text-slate-900 text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                    <SafeIcon icon="🔗" className="w-4 h-4 text-primary-600" /> Delingslink
                  </h3>
                  <div className="flex gap-2 p-2 bg-slate-50 border border-slate-200 rounded-xl mb-4">
                    <input readOnly value={selectedContract.document_url || ''} className="flex-1 bg-transparent border-none text-xs font-mono text-slate-500 focus:ring-0 truncate" />
                    <button onClick={() => handleCopyLink(selectedContract.document_url)} className={`p-2 rounded-lg transition-all ${copied ? 'bg-emerald-500 text-white' : 'hover:bg-slate-200 text-slate-400'}`}>
                      <SafeIcon icon={copied ? "✅" : "📋"} className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <a href={selectedContract.document_url || '#'} target="_blank" rel="noreferrer" className="flex-1 py-3 bg-slate-900 text-white text-xs font-black uppercase tracking-widest rounded-xl text-center hover:bg-slate-800 transition-all flex items-center justify-center gap-2">
                      <SafeIcon icon="↗️" className="w-4 h-4" /> Åpne i ny fane
                    </a>
                  </div>
                </div>

                <div className="bg-primary-50 p-6 rounded-[2rem] border border-primary-100">
                  <h4 className="font-black text-primary-900 text-sm mb-2">Test sporingen</h4>
                  <p className="text-xs text-primary-700 mb-4 font-medium">Klikk her for å simulere at kunden åpner dokumentet nå.</p>
                  <button onClick={() => simulateDocumentView(selectedContract.id)} className="w-full py-3 bg-primary-600 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-200 transition-all active:scale-95">
                    Simulere visning
                  </button>
                </div>

                <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary-600/20 rounded-full -mr-16 -mt-16 blur-3xl"></div>
                  <div className="flex justify-between items-center mb-8 relative z-10">
                    <h3 className="text-lg font-black tracking-tight">Sporingslogg</h3>
                    <div className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-300">Sanntid</div>
                  </div>
                  <div className="space-y-8 relative z-10">
                    {(selectedContract.tracking_log || []).slice().reverse().map((log, i) => (
                      <div key={i} className="flex gap-4 relative group">
                        {i !== (selectedContract.tracking_log?.length || 0) - 1 && (
                          <div className="absolute left-[11px] top-6 bottom-[-32px] w-[2px] bg-white/10"></div>
                        )}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ${log.action === 'Signed' ? 'bg-emerald-500' : log.action === 'Opened' || log.action === 'Viewed' ? 'bg-blue-500' : 'bg-slate-700'}`}>
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-1">
                            <p className="font-black text-sm">{log.action}</p>
                            <p className="text-[10px] text-slate-400 font-bold">{log.date?.split('T')[1]?.substring(0, 5) || 'Nylig'}</p>
                          </div>
                          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">{formatDate(log.date)}</p>
                          {log.location && (
                            <div className="space-y-2 p-3 bg-white/5 rounded-xl border border-white/5 group-hover:bg-white/10 transition-colors">
                              <div className="flex items-center gap-2 text-[10px] text-slate-300 font-black uppercase tracking-widest">
                                <SafeIcon icon="📍" className="w-3 h-3 text-primary-400" /> {log.location}
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-bold">
                                  <SafeIcon icon={log.device?.includes('iPhone') ? "📱" : "💻"} className="w-3 h-3" /> {log.device || 'Ukjent enhet'}
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono">
                                  <SafeIcon icon="🌐" className="w-3 h-3" /> {log.ip}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2.5rem] p-12 text-center flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4">
                  <SafeIcon icon="📊" className="w-8 h-8 text-slate-300" />
                </div>
                <h3 className="text-lg font-black text-slate-900">Ingen valgt kontrakt</h3>
                <p className="text-xs text-slate-500 font-medium mt-1 max-w-[200px] mx-auto">Velg en kontrakt fra listen for å se detaljert sporingshistorikk.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <SendContractModal isOpen={isSendModalOpen} onClose={() => setIsSendModalOpen(false)} />
    </div>
  );
};

export default SentContracts;